import { CSSProperties } from 'react';

/**
 * Layout styles for the SINGLE-TABLE game page's two rows. Extracted from
 * Game.tsx so the stacking relationship between the felt and the action footer
 * has ONE definition that both production and Storybook read.
 *
 * (Grid-cell layout is the sibling concern and lives in gameGridLayout.ts.)
 *
 * The page is a flex COLUMN: [tableRow (flex:1)] then [footerRow (content
 * height)].
 *
 * WHY THIS IS SHARED, NOT INLINED (EN-10420)
 * ------------------------------------------
 * `footerRow` carries `position: relative` + `zIndex: 10`, which makes it a
 * stacking context. The bet/raise tray lives inside it (absolute, bottom:100%,
 * its own zIndex: 50 — RaiseActionExpanded) and expands UPWARD over the felt.
 * The POV seat sits inside `tableRow` at zIndex 4
 * (Seat/modes/getPortraitModeStyles.ts), and `tableRow` declares no z-index of
 * its own — so the ENTIRE footer subtree paints above the ENTIRE POV seat.
 * Raising the seat's local z-index cannot cross that boundary.
 *
 * `tableRow` additionally clips (`overflow: hidden`), which is a second and
 * independent mechanism: it cuts the POV cards at the row boundary even with
 * the tray closed.
 *
 * Those two facts are the whole of EN-10420 (tray covers the POV's hole cards,
 * action timer, and hand-strength label). A story that hand-copies these
 * numbers can drift from production and "prove" a fix that does not hold —
 * which is exactly why the pre-existing RaiseAction stories, which mount the
 * tray against a mock footer slot, could not validate the bug. Import from here
 * instead so the story and the page cannot disagree.
 */
/**
 * The page shell around the two rows — Game.tsx's outermost flex column.
 * Shared for the same anti-drift reason as the rows: the shell owns the
 * gradient that paints under the iPhone status bar and the
 * `env(safe-area-inset-top)` padding that keeps content below the notch —
 * the top half of the device safe-zone story (footerRow's paddingBottom is
 * the bottom half). A story that fakes the shell shows a hard top edge and
 * loses the safe-zone geometry the EN-10420 fix must be designed against.
 *
 * `height: '100%'` resolves against index.css's `html, body, #root
 * { height: 100% }` chain in the app. Storybook's preview.css un-pins that
 * chain, so stories must wrap this shell in their own definite-height stage.
 */
export const pageShell: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  maxWidth: '100%',
  background: 'linear-gradient(180deg, #1c1a14 0%, #12100c 100%)',
  color: 'white',
  position: 'relative',
  paddingTop: 'env(safe-area-inset-top, 0)'
};

export const tableRow: CSSProperties = {
  flex: 1,
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  /**
   * Reserve the top chrome rather than being painted under it.
   *
   * The "Your Tables" strip / Add Table button render in an overlay SIBLING at
   * z-index 50, and this row has no z-index of its own and clips its overflow —
   * so it could neither out-rank the chrome nor grow to dodge it, and the top
   * seats were simply placed underneath it, cards and all. Raising this row
   * instead would put seats over the navigation, which is worse.
   *
   * Padding is the honest fix: the felt gets a smaller box, and because seat
   * positions are percentages OF that box the whole ring re-lays itself out
   * inside the reserved area with no further plumbing. Defaults to 0, so any
   * layout that renders no strip is unchanged.
   *
   * Published by hooks/useChromeInset; see there for why it is a CSS variable
   * and why it cannot cause a resize loop.
   */
  paddingTop: 'var(--table-chrome-inset, 0px)'
};

/**
 * The bottom taskbar holding UserActions. Content-height (never flex-grown),
 * and the stacking context the bet/raise tray renders into — see the module
 * docblock for why `zIndex` here outranks anything inside `tableRow`.
 */
export const footerRow: CSSProperties = {
  zIndex: 10,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#12100c',
  borderTop: '1px solid rgba(212, 168, 83, 0.12)',
  padding: '0.3rem 0.5rem',
  paddingBottom: 'max(0.3rem, env(safe-area-inset-bottom, 0px))',
  gap: '0.35rem',
  flexShrink: 0
};
